/* ABAC policy bundles: what is stored, what is running, and whether the two agree. D-10.
 *
 * Built to the reference design, whose structure *is* the argument. Header and runtime state, then the
 * two things a reader has to be told before reading anything else, then the stored bundles, then one
 * bundle's versions, then what this process has actually instantiated. Somebody arriving because a
 * decision went the wrong way reads down and stops when they have their answer.
 *
 * **Two lists, because they answer two different questions and can legitimately disagree.** The
 * bundles table is what this tenant has *stored*. The running panel is what this *process* has
 * instantiated, keyed on the content hash of the bytes it is executing. `ActiveBundleRegistry` is a
 * lazy cache by design — the registry is not transactional and the publish is — so a freshly-activated
 * bundle reads as absent until something evaluates. §9 records that reading like a failure when it is
 * not, so the screen says so rather than leaving a reader to guess.
 *
 * **The opt-in state is the most important thing on the page.** ABAC is `LUMIQ_ABAC_ENABLED` and must
 * stay opt-in: a tenant with no active bundle raises `NoActiveBundleError` and `AuthorizationService`
 * fails closed, so wiring the evaluator unconditionally would deny **everything** for every tenant
 * that has not uploaded a bundle — which is every tenant on every existing install. So "no runtime" is
 * a deployment fact rather than an error.
 *
 * **No upload control, and the reference says so in words rather than leaving the absence to be
 * discovered.** An artifact reaches this platform as compiled OPA WASM through a reviewed pipeline,
 * and a file picker is the affordance that makes somebody skip one. Creating a bundle *identity* is
 * harmless and is a different act from uploading bytes that will run — which is why that form exists
 * and an upload does not.
 */

import { Fragment, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';

import type { BumpKind } from '../api/policy';
import type { EnforcementReading } from '../model/bundles';
import type {
  BundleVersion,
  PolicyBundle,
  VersionPolicy,
} from '@/shared/api/generated/policy';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { activeSemverOf } from '../model/bundles';
import { PolicyBuilder } from './PolicyBuilder';
import { SectionHead } from '@/shared/ui/SectionHead';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useActivateVersion,
  useAbacPosture,
  useActivations,
  useActiveBundles,
  useBundleVersions,
  useCreateBundle,
  useDeleteBundle,
  useDeprecateVersion,
  useEnforcement,
  useSetAbacEnabled,
  useVersionPolicy,
  usePolicyBundles,
  usePublishBundle,
  versionsQuery,
} from '../api/policy';
import {
  actionFor,
  agreementFor,
  AGREEMENT_MARK,
  bundleDraftHintKey,
  bundleDraftIsValid,
  EMPTY_BUNDLE_DRAFT,
  deletabilityOf,
  deleteBlockedKey,
  enforcementSummary,
  modeLabelKey,
  NARROWING,
  RAIL_TONE,
  readingStrip,
  noActionKey,
  notLoadedReasonKey,
  RUN_TONE,
  runningHashesOf,
  postureOriginKey,
  runStateOf,
  semverOf,
  shortHash,
} from '../model/bundles';
import type { BundleDraft } from '../model/bundles';

/** How many bundles' version lists to read for the table's own columns.
 *
 * **The table needs per-bundle version data and the list endpoint does not carry it.** `PolicyBundle`
 * is `{id, name, description}`, so "3 versions" and the Running chip each need
 * `GET /bundles/{id}/versions`. That is an N+1, bounded here and disclosed on the page rather than
 * left to grow: a tenant has a handful of policy bundles, and the alternative — adding a count and an
 * active hash to the listing — is a backend change to a contract for a screen that can state its own
 * bound. Past the cap the two columns read "not read" rather than a figure nothing measured.
 */
const MAX_VERSION_LOOKUPS = 12;

/** The chip in the Running column: does this bundle's activated version match what is executing. */
function RunChip({
  versions,
  runningHashes,
}: {
  readonly versions: readonly BundleVersion[] | undefined;
  readonly runningHashes: ReadonlySet<string>;
}): ReactElement {
  const { t } = useTranslation();

  /* Undefined is not empty: the per-bundle read is pending or past the cap, so this says "not read"
   * rather than "no active version" — which would be a statement about the tenant's configuration
   * made on the strength of a request that has not happened. */
  if (versions === undefined) {
    return <span className="lq-muted">{t('policy.run.unread')}</span>;
  }

  const state = runStateOf(versions, runningHashes);
  return (
    <span
      className={`lq-tag lq-tag--run-${RUN_TONE[state]}`}
      title={t(`policy.run.${state}.meaning`)}
    >
      <span className="lq-tag__dot" aria-hidden="true" />
      {t(`policy.run.${state}`)}
    </span>
  );
}

const NAME_FIELD_ID = 'policy-bundle-name';
/* One id, because the label and the input both need it and a mismatch is silent: the field simply
   has no accessible name, and only a test that queries by label would notice. This form is rendered
   once, so a constant is honest — a second instance would need a generated id. */

/** What the reserved bundle name means, beside the field where it matters.
 *
 * **A popover and deliberately not a modal.** The reader is mid-form and the answer is one
 * paragraph: a modal would take the field away to explain the field, and they would have to dismiss
 * it before typing what it told them to type.
 *
 * **It exists because the naming trap cost somebody a whole policy.** Authorization consults
 * exactly one bundle name, the constant lives in the composition root where nobody would look, and
 * this form's own placeholder suggested a different one — so rules built under any other name are
 * stored, versioned, published, activated, and consulted by nothing. The table carries a per-row
 * marker for bundles already in that state; this is what stops it happening in the first place.
 *
 * `aria-expanded` and a real `button`, so the keyboard route is the same as the pointer one. Closing
 * on a second press rather than on blur: a reader who tabs into the paragraph to read it must not
 * have it vanish under them.
 */
function AuthzNameInfo({
  governingName,
}: {
  readonly governingName: string;
}): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <span className="lq-hintwrap">
      <button
        type="button"
        className="lq-hintbtn"
        aria-expanded={open}
        aria-label={t('policy.form.nameInfo.label')}
        onClick={() => {
          setOpen((shown) => !shown);
        }}
      >
        i
      </button>
      {open && (
        <span className="lq-hintpop" role="note">
          {/* The governing name comes from `/enforcement` rather than from a literal here, so this
              paragraph cannot name a bundle a differently-configured deployment does not consult. */}
          <span className="lq-hintpop__body">
            {t('policy.form.nameInfo.governing', { name: governingName })}
          </span>
          <span className="lq-hintpop__body">
            {t('policy.form.nameInfo.others', { name: governingName })}
          </span>
        </span>
      )}
    </span>
  );
}

/** The create-identity form, inline under the Stored bundles heading as the reference has it. */
function CreateBundleForm({
  onClose,
  onCreated,
  governingName,
}: {
  readonly onClose: () => void;
  readonly onCreated: (name: string) => void;
  readonly governingName: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const create = useCreateBundle();
  const [draft, setDraft] = useState<BundleDraft>(EMPTY_BUNDLE_DRAFT);
  const valid = bundleDraftIsValid(draft);

  return (
    <div className="lq-inlineform">
      <div className="lq-inlineform__title">{t('policy.form.title')}</div>
      <p className="lq-inlineform__intro">{t('policy.form.intro')}</p>

      {/* 280px and then the rest, as the reference sets it: a bundle name is a dotted identifier of
          predictable length and the description is a sentence, so an even split would leave one field
          padded and the other cramped. */}
      <div className="lq-inlineform__grid-name">
        {/* **A `div` rather than a `label`, and the info button is a sibling of the label rather
            than inside it.** Two reasons, and the second is the one a gate cannot see: a `<button>`
            inside a `<label>` contributes its text to the *input's* accessible name, and a click
            anywhere in a label activates its control — so pressing the "i" would also focus the
            field. Explicit `htmlFor`/`id` keeps the association a screen reader needs. */}
        <div className="lq-inlineform__field">
          <div className="lq-inlineform__labelrow">
            <label className="lq-inlineform__label" htmlFor={NAME_FIELD_ID}>
              {t('policy.form.name')}
            </label>
            {/* Only once the governing name has been read. A hint that named the wrong bundle would
                be worse than no hint, and a placeholder like `authz` written here as a literal is
                how that happens. */}
            {governingName !== null && <AuthzNameInfo governingName={governingName} />}
          </div>
          <input
            id={NAME_FIELD_ID}
            className="lq-mono"
            type="text"
            value={draft.name}
            placeholder={t('policy.form.namePlaceholder')}
            onChange={(event) => {
              setDraft((current) => ({ ...current, name: event.target.value }));
            }}
          />
        </div>
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('policy.form.description')}</span>
          <input
            type="text"
            value={draft.description}
            placeholder={t('policy.form.descriptionPlaceholder')}
            onChange={(event) => {
              setDraft((current) => ({ ...current, description: event.target.value }));
            }}
          />
        </label>
      </div>

      {create.isError && (
        /* The server's own words. A duplicate name is a 409 explaining that the evaluator's cache is
           keyed on it, and restating that here would be a second place deciding what it means. */
        <Alert tone="error" title={t('policy.create.failed')}>
          {create.error.message}
        </Alert>
      )}

      <div className="lq-inlineform__foot">
        <div className={valid ? 'lq-inlineform__summary' : 'lq-inlineform__blocked'}>
          {t(bundleDraftHintKey(draft))}
        </div>
        <button type="button" className="lq-inlineform__cancel" onClick={onClose}>
          {t('policy.form.cancel')}
        </button>
        <button
          type="button"
          className="lq-inlineform__submit"
          disabled={!valid || create.isPending}
          onClick={() => {
            const name = draft.name.trim();
            create.mutate(
              { name, description: draft.description.trim() },
              {
                onSuccess: () => {
                  setDraft(EMPTY_BUNDLE_DRAFT);
                  onCreated(name);
                },
              },
            );
          }}
        >
          {t('policy.form.create')}
        </button>
      </div>
    </div>
  );
}

/** Whether policy is actually deciding anything. **The page's thesis, and the thing that lied.**
 *
 * This screen used to render one line from `runtime_available`, which means only that the wasmtime
 * wheel is installed — so on a deployment with the wheel present and the evaluator unwired it
 * announced "Policy runtime available" while no bundle could affect any decision. Five readings
 * now, because there are five situations and each needs a different person to do a different thing;
 * `enforcementSummary` decides which, in the model, where it can be tested.
 *
 * Rendered only once the read has landed: "nothing is enforced" is a claim about the deployment and
 * a pending request is not evidence for it.
 */
function EnforcementBanner(): ReactElement | null {
  const { t } = useTranslation();
  const enforcement = useEnforcement();

  if (enforcement.isError) {
    /* A failed read is reported as a failure of the request, never as an absence of enforcement.
       Reporting "nothing is enforced" from a 500 would be a statement about the policy that a
       broken request has no grounds for — the defect this codebase records at four screens. */
    return (
      <Alert tone="error" title={t('policy.enf.unread')}>
        {enforcement.error.message}
      </Alert>
    );
  }
  if (!enforcement.isSuccess) return null;

  const state = enforcement.data;
  const summary = enforcementSummary(state);
  const accent = `var(${RAIL_TONE[summary.tone]})`;

  return (
    <div
      className="lq-ap__ef"
      role="status"
      /* The accent is set as a custom property rather than as five class variants, because the
         band needs it in three places — the top edge, the dot and the headline — and a variant
         class per tone would be fifteen rules saying one thing. `--ef-glow` is the same colour at
         low alpha through `color-mix`, so the ring cannot drift from the dot it surrounds. */
      style={
        {
          '--ef-edge': accent,
          '--ef-glow': `color-mix(in srgb, ${accent} 30%, transparent)`,
        } as CSSProperties
      }
    >
      <div className="lq-ap__efHead">
        <div className="lq-ap__efMain">
          <div className="lq-ap__efLabel">{t('policy.ef.eyebrow')}</div>
          <div className="lq-ap__efReading">
            <span className="lq-ap__efDot" aria-hidden="true" />
            <span className="lq-ap__efHeadline">
              {t(summary.titleKey, {
                bundle: state.bundle_name,
                semver: state.semver ?? '',
              })}
            </span>
          </div>
          {/* **One sentence about the state, not two.** The reading's own detail and the
              platform's `detail` field say the same thing in different words, and the band printed
              both — so a reader met "the bundle has no active version" twice and took the second
              for a separate finding. The reading's wins, because it is what the strip beside it is
              indexed on. */}
          <div className="lq-ap__efDetail">
            {t(summary.bodyKey, {
              bundle: state.bundle_name,
              semver: state.semver ?? '',
            })}
          </div>
        </div>

        {/* **All five readings, with this tenant's lit.** The single most valuable thing the
            redesign adds: the old banner showed one sentence, so a reader had no way to know the
            other four states existed or how far from enforcing they were. Seeing `RBAC only` as
            one cell of five is what stops it reading as an error.

            A `<ul>` rather than divs, because it is a list of five things and one of them is
            current — and the current one carries a word in its `title`, so the distinction is not
            colour alone. */}
        <ul className="lq-ap__efStrip">
          {readingStrip(summary.reading).map((cell) => (
            <li
              key={cell.reading}
              className={cell.on ? 'lq-ap__efCell lq-ap__efCell--on' : 'lq-ap__efCell'}
              aria-current={cell.on ? 'true' : undefined}
              title={t(cell.on ? 'policy.reading.current' : 'policy.reading.other')}
              /* **The lit cell is filled with its own reading's colour**, so which state this
                 tenant is in and what kind of state it is are one mark rather than two. A single
                 fixed highlight — which this was — makes every reading look alike when lit, which
                 is exactly the distinction the strip exists to draw: `RBAC only` lit in the same
                 blue as `Failing closed` would read as the same kind of news. */
              style={{ background: cell.on ? `var(${cell.tokenName})` : undefined }}
            >
              <span
                className="lq-ap__efCellDot"
                aria-hidden="true"
                /* **An unlit cell's dot carries its own colour and the lit one's goes dark**, which
                   is the way round the design has it and the way round that carries information:
                   the four states this tenant is *not* in still say what they would mean, and the
                   lit cell needs no dot of its own because the whole cell is that colour.
                   No `as CSSProperties`, because `background` is a real key. */
                style={{
                  background: cell.on
                    ? 'var(--rail-ink-strong)'
                    : `var(${cell.tokenName})`,
                }}
              />
              <span className="lq-ap__efCellLabel">{t(cell.labelKey)}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* **The naming trap, said out loud, and unconditionally.** Only a bundle of this name is
          consulted; the constant lives in the composition root where nobody would look, and the
          create form's own placeholder suggests a different one. It was once shown only when
          scoping was on, reasoned as "that is when getting the name wrong costs something" — the
          opposite is true, because somebody *building* a policy needs the rule while the name can
          still be changed for free. */}
      <div className="lq-ap__efFoot">
        {t('policy.ef.governedBy', { name: state.bundle_name })}
      </div>
    </div>
  );
}

/** What decides a request here, and the control that changes it.
 *
 * **The panel the specification asks for, and the reason it is a panel rather than a line.** A
 * reader must be able to see the tenant's posture without inferring it from an error or a banner:
 * "RBAC only" or "RBAC + Attribute Policy", stated in those words, from the server's own `mode`.
 *
 * **Three operations, named side by side, because they were confusable and are not
 * interchangeable.** Disabling stops scoping deciding and roles decide alone. Deprecating the last
 * active version fails *closed* and refuses everything — which is why it was never an off switch and
 * why one had to exist. Deleting removes a bundle identity and changes enforcement not at all. A
 * reader who reaches for the wrong one of those three gets a very different outcome, so all three
 * are described here rather than discovered by pressing.
 *
 * **The toggle is two buttons rather than a checkbox.** A checkbox's state and its label read as one
 * thing, so "Attribute policy" beside an unchecked box is ambiguous about whether it describes what
 * is or what pressing does. Two labelled buttons, with the current one marked and disabled, cannot
 * be misread — and this is a change to how a whole tenant is governed.
 */
function EnforcementModePanel(): ReactElement | null {
  const { t } = useTranslation();
  const posture = useAbacPosture();
  const setEnabled = useSetAbacEnabled();
  /* The same query the band above uses, so this costs no second request — React Query dedupes on
     the key. Read here rather than passed down because the bars are *this* panel's argument and a
     prop would let a future caller draw them from something else. */
  const enforcement = useEnforcement();

  if (posture.isError) {
    /* A failed read is reported as a failure of the request. Rendering a toggle in some default
       position would state a posture this screen has no grounds for, and offer to "change" it to
       the value it is already at. */
    return (
      <Alert tone="error" title={t('policy.abac.unread')}>
        {posture.error.message}
      </Alert>
    );
  }
  if (!posture.isSuccess) return null;

  const current = posture.data;
  const busy = setEnabled.isPending;

  return (
    <div className="lq-modepanel">
      <div className="lq-modepanel__head">
        <div>
          <div className="lq-modepanel__label">{t('policy.mode.heading')}</div>
          <div className="lq-modepanel__value">{t(modeLabelKey(current.mode))}</div>
          {/* Whose answer it is. A defaulted posture rendered as a configured one reads as a
              decision somebody made, which is a different conversation with a customer. */}
          <div className="lq-modepanel__origin">
            {t(postureOriginKey(current), {
              actor: current.updated_by ?? '',
            })}
          </div>
        </div>
        <div
          className="lq-modepanel__switch"
          role="group"
          aria-label={t('policy.mode.heading')}
        >
          <button
            type="button"
            className={
              current.enabled
                ? 'lq-modepanel__opt'
                : 'lq-modepanel__opt lq-modepanel__opt--on'
            }
            disabled={busy || !current.enabled}
            onClick={() => {
              setEnabled.mutate(false);
            }}
          >
            {t('policy.mode.rbacOnly')}
          </button>
          <button
            type="button"
            className={
              current.enabled
                ? 'lq-modepanel__opt lq-modepanel__opt--on'
                : 'lq-modepanel__opt'
            }
            disabled={busy || current.enabled}
            onClick={() => {
              setEnabled.mutate(true);
            }}
          >
            {t('policy.mode.rbacAndPolicy')}
          </button>
        </div>
      </div>

      {setEnabled.isError && (
        /* The server's own words. `policy.bundle:configure` reaches `admin` alone, and restating
           that here would be a second place deciding who may do this. */
        <Alert tone="error" title={t('policy.abac.failed')}>
          {setEnabled.error.message}
        </Alert>
      )}

      {/* **The three operations, distinguished.** Not a tooltip and not a link: a reader deciding
          between them is deciding now, and the difference between "roles decide" and "everything is
          refused" is not something to discover afterwards. */}
      {/* **The signature element**, and the one part of this screen that is an argument rather
          than a layout. Two stacked bars: the role's grant, and what policy permits inside it.

          Drawn only once the reading has landed. A pair of bars is a claim about what is
          currently narrowing what, and a pending request is not evidence for it — the same reason
          the band above renders nothing until it has an answer. */}
      {/* `semver ?? null` because the wire type is `string | null | undefined` — absent when the
          field is omitted, null when nothing is loaded, and both mean "no version to name". The
          model's `semverOf` records the same three-spellings trap for a version row; collapsing it
          at this boundary keeps the component's prop two-state. */}
      {enforcement.isSuccess && (
        <NarrowingBars
          reading={enforcementSummary(enforcement.data).reading}
          bundle={enforcement.data.bundle_name}
          semver={enforcement.data.semver ?? null}
        />
      )}

      {/* **Cards rather than a list**, because a reader here is *comparing* three operations whose
          outcomes differ wildly — roles decide / everything is refused / nothing changes — and a
          bulleted paragraph makes them read as one explanation. */}
      <div className="lq-ap__ops">
        <div className="lq-ap__op">
          <div className="lq-ap__opName">{t('policy.ops.disable.name')}</div>
          <div className="lq-ap__opBody">{t('policy.ops.disable.effect')}</div>
        </div>
        <div className="lq-ap__op">
          {/* The only one of the three that refuses every request, and the only one tinted. */}
          <div className="lq-ap__opName lq-ap__opName--warn">
            {t('policy.ops.deprecate.name')}
          </div>
          <div className="lq-ap__opBody">{t('policy.ops.deprecate.effect')}</div>
        </div>
        <div className="lq-ap__op">
          <div className="lq-ap__opName">{t('policy.ops.delete.name')}</div>
          <div className="lq-ap__opBody">{t('policy.ops.delete.effect')}</div>
        </div>
      </div>
    </div>
  );
}

/** The two bars. **Never wider below than above, and that is structural rather than arithmetic.**
 *
 * The width comes from `NARROWING`, a total map over the five readings, and the CSS clamps it with
 * `min(100%, …)` besides — so there is no value this component can be handed that draws attribute
 * policy as reaching further than the grant it sits inside.
 *
 * The lower bar's tone is its own rather than the reading's: `neutral` when policy is not
 * consulted at all, `error` when it is consulted and refuses everything, and the accent when it is
 * genuinely narrowing. Those are three different facts and a single tone would collapse the first
 * two, which are the two most easily confused states on the whole screen.
 */
function NarrowingBars({
  reading,
  bundle,
  semver,
}: {
  readonly reading: EnforcementReading;
  readonly bundle: string;
  readonly semver: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const bar = NARROWING[reading];
  const fill = {
    neutral: 'var(--surface-sunken)',
    error: 'var(--err-bg)',
    enforcing: 'color-mix(in srgb, var(--accent) 18%, var(--surface-raised))',
  }[bar.tone];

  return (
    <>
      <div className="lq-ap__narrow">
        <div className="lq-ap__bar lq-ap__bar--rbac">
          <span className="lq-ap__barLabel">{t('policy.narrow.rbac')}</span>
        </div>
        <div
          className="lq-ap__bar lq-ap__bar--abac"
          style={{ '--abac-width': bar.width, background: fill } as CSSProperties}
        >
          <span className="lq-ap__barLabel">
            {/* Both arguments always, because ICU refuses an absent one and the enforcing caption
                is the only reading that names a version. An empty semver never renders: no other
                caption interpolates it. */}
            {t(bar.captionKey, { bundle, semver: semver ?? '' })}
          </span>
        </div>
      </div>
      <div className="lq-ap__narrowNote">{t('policy.narrow.note')}</div>
    </>
  );
}

/** The control that *opens* the confirmation, or says why it cannot.
 *
 * **Blocked while a version is active, and the reason is on the control before it is pressed.** The
 * API refuses with a 409 and the exact sentence; a disabled button with no explanation would make a
 * reader press it to find out. `deletabilityOf` decides in the model, where it is tested — including
 * that "versions not read yet" is not "no active version".
 *
 * The confirmation itself is `DeleteConfirmRow`, a row of its own — see there for why.
 */
function DeleteBundleControl({
  versions,
  open,
  onOpen,
}: {
  readonly versions: readonly BundleVersion[] | undefined;
  readonly open: boolean;
  readonly onOpen: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const blocked = deleteBlockedKey(deletabilityOf(versions));

  if (blocked !== null) {
    return (
      /* **It says "Delete unavailable" rather than "Delete".** A control reading like the live one
         and merely refusing to work is a control somebody presses twice and then reports as broken;
         naming the state is what makes the tooltip worth reading. */
      <span className="lq-rowblocked" title={t(blocked)}>
        {t('policy.delete.unavailable')}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="lq-rowaction lq-rowaction--danger"
      aria-expanded={open}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      {t('policy.delete.open')}
    </button>
  );
}

/** The confirmation, as a full-width row beneath the bundle it is about.
 *
 * **A row rather than a dialog or an in-cell block, and the reason is the sentence it has to
 * carry.** The one thing a reader is most likely to get wrong about deleting the governing bundle
 * is that it does *not* switch enforcement off — and that takes a sentence, which in the actions
 * cell sets that column's width and wraps to a column of single words. A dialog would cover the
 * table that gives the row its context. As a row it gets the whole table and keeps the bundle's
 * name, version count and running state visible directly above it.
 */
function DeleteConfirmRow({
  bundle,
  versions,
  onCancel,
  onDeleted,
}: {
  readonly bundle: PolicyBundle;
  /* **Never undefined, and that is a guarantee rather than an assumption.** A bundle whose versions
     have not been read has `deletabilityOf === 'unknown'`, so its control is blocked and this row
     is unreachable — which is why there is no sentence here for a count the page does not have.
     The type says so, so the caller has to prove it. */
  readonly versions: readonly BundleVersion[];
  readonly onCancel: () => void;
  readonly onDeleted: (name: string, note: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const remove = useDeleteBundle();

  return (
    <td colSpan={5} className="lq-ap__drawer">
      <div className="lq-ap__confirm">
        <div className="lq-ap__confirmTitle">
          {t('policy.delete.confirmTitle', { name: bundle.name })}
        </div>
        {/* **What will be deleted, and — the sentence that matters most — what will not change.**
            The count is the versions the table already loaded, which is a real count rather than a
            guess precisely because an unread bundle cannot get here. The server says the
            unchanged-enforcement half too, on the response, so a screen that dropped this line
            still could not claim the opposite. */}
        <div className="lq-ap__confirmBody">
          {t('policy.delete.confirmBody', { count: versions.length })}
        </div>
        {remove.isError && (
          <Alert tone="error" title={t('policy.delete.failed')}>
            {remove.error.message}
          </Alert>
        )}
        <div className="lq-ap__confirmActs">
          <button
            type="button"
            className="lq-btn lq-btn--secondary"
            disabled={remove.isPending}
            onClick={() => {
              remove.reset();
              onCancel();
            }}
          >
            {t('policy.delete.cancel')}
          </button>
          <button
            type="button"
            className="lq-btn lq-btn--danger"
            disabled={remove.isPending}
            onClick={() => {
              remove.mutate(bundle.id, {
                onSuccess: (result) => {
                  /* Named from the *response* rather than from what this component believed it
                     was deleting, and the note is host-written — so the flash cannot say
                     something the server did not. */
                  onDeleted(result.deleted.name, result.note);
                },
              });
            }}
          >
            {t('policy.delete.do')}
          </button>
        </div>
      </div>
    </td>
  );
}

/** One bundle's activation history: append-only, newest first.
 *
 * **The read this module was built around and never had.** `policy.activation_log` is append-only
 * by grant precisely so an auditor can answer "what was permitted on 3 March"; nothing exposed it,
 * so the record was write-only and that question had no surface anywhere in the product.
 *
 * Below the versions table rather than beside it: the versions are what a reader acts on, and this
 * is what they check afterwards.
 */
function ActivationsPanel({ bundle }: { readonly bundle: PolicyBundle }): ReactElement {
  const { t } = useTranslation();
  const activations = useActivations(bundle.id);

  if (activations.isPending) return <SkeletonRows rows={2} columns={5} />;
  if (activations.isError) {
    return (
      <Alert tone="error" title={t('policy.activations.failed')}>
        {activations.error.message}
      </Alert>
    );
  }
  if (activations.data.length === 0) {
    return (
      <EmptyState
        title={t('policy.activations.emptyTitle')}
        guidance={t('policy.activations.empty')}
      />
    );
  }

  return (
    <>
      <p className="lq-note">{t('policy.activations.intro')}</p>
      <div className="lq-table-scroll lq-table-scroll--framed">
        <table className="lq-table lq-table--ledger">
          <caption className="lq-visually-hidden">
            {t('policy.activations.caption', { bundle: bundle.name })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('policy.activations.column.semver')}</th>
              <th scope="col">{t('policy.activations.column.replaced')}</th>
              <th scope="col">{t('policy.activations.column.hash')}</th>
              <th scope="col">{t('policy.activations.column.actor')}</th>
              <th scope="col">{t('policy.activations.column.when')}</th>
            </tr>
          </thead>
          <tbody>
            {activations.data.map((entry) => (
              <tr key={`${entry.semver}-${entry.activated_at}`}>
                <td className="lq-mono lq-nowrap">{entry.semver}</td>
                <td className="lq-mono lq-nowrap">
                  {/* Null is "there was nothing before", which is a different fact from an omitted
                      field — so it gets a sentence rather than a dash. */}
                  {entry.previous_semver === null ? (
                    <span className="lq-muted">{t('policy.activations.first')}</span>
                  ) : (
                    entry.previous_semver
                  )}
                </td>
                <td>
                  <span className="lq-ap__hash lq-mono" title={entry.content_hash}>
                    {shortHash(entry.content_hash)}
                  </span>
                </td>
                {/* The person, not the tenancy: migration 0028 widened this column because every
                    activation had been attributed to the organisation. */}
                <td title={entry.actor}>{entry.actor}</td>
                <td className="lq-nowrap">
                  {new Date(entry.activated_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** One bundle's versions, and the lifecycle steps a reader may take. */
function VersionsTable({
  bundle,
  runningHashes,
  onActed,
}: {
  readonly bundle: PolicyBundle;
  readonly runningHashes: ReadonlySet<string>;
  readonly onActed: (
    titleKey: string,
    bodyKey: string,
    values: Record<string, string>,
  ) => void;
}): ReactElement {
  const { t } = useTranslation();
  const versions = useBundleVersions(bundle.id);
  const publish = usePublishBundle();
  const activate = useActivateVersion();
  const deprecate = useDeprecateVersion();
  const active = useActiveBundles();
  /* Which reason a not-running active version gets depends on whether anything will ever load one,
     so the flag is read here rather than assumed. See `notLoadedReasonKey`. */
  const abacEnabled = active.isSuccess && active.data.abacEnabled;
  const failure = publish.error ?? activate.error ?? deprecate.error;
  /* **Unchosen until somebody chooses**, rather than defaulted to `minor`. A bundle has at most
     one draft, and this table is one bundle's versions, so one piece of state is the whole need. */
  const [bump, setBump] = useState<BumpKind | null>(null);
  /* **One open at a time, keyed on the semver.** A set would let somebody open every version and
     fire a request per row on a bundle with a long history; a single key means opening the next one
     closes the last, which is also what a reader comparing two versions actually does. `null` is
     "none open" rather than an empty string, so a draft — whose semver *is* null — cannot be
     confused with the closed state. */
  const [rulesOpen, setRulesOpen] = useState<string | null>(null);

  if (versions.isPending) return <SkeletonRows rows={3} columns={6} />;
  if (versions.isError) {
    return (
      <Alert tone="error" title={t('policy.versions.failed')}>
        {versions.error.message}
      </Alert>
    );
  }
  if (versions.data.length === 0) {
    return (
      <EmptyState
        title={t('policy.versions.emptyTitle')}
        guidance={t('policy.versions.empty')}
      />
    );
  }

  return (
    <>
      {failure !== null && (
        <Alert tone="error" title={t('policy.lifecycle.failed')}>
          {failure.message}
        </Alert>
      )}
      <div className="lq-table-scroll lq-table-scroll--framed">
        <table className="lq-table lq-table--ledger">
          <caption className="lq-visually-hidden">
            {t('policy.versions.caption', { bundle: bundle.name })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('policy.column.semver')}</th>
              <th scope="col">{t('policy.column.state')}</th>
              <th scope="col">{t('policy.column.hash')}</th>
              <th scope="col" className="lq-table__numeric">
                {t('policy.column.bytes')}
              </th>
              <th scope="col">{t('policy.column.created')}</th>
              <th scope="col" className="lq-table__actions" />
            </tr>
          </thead>
          <tbody>
            {versions.data.map((version) => {
              const action = actionFor(version);
              /* Read once. `actionFor` has already decided a reactivate is possible, which means this
                 is non-null — but reading it again inside the handler would be a second judgement
                 that could disagree with the first. */
              const semver = semverOf(version);
              const isRunning =
                version.state === 'active' && runningHashes.has(version.content_hash);
              const busy = publish.isPending || activate.isPending || deprecate.isPending;

              return (
                /* A fragment because the row can be followed by its own rules drawer, and the key
                   moves here: two top-level siblings per iteration need one key between them. */
                <Fragment key={version.content_hash}>
                  {/* The active row is tinted, as the reference tints it: on a list of kept history
                      the one in force is what a reader came to check. */}
                  <tr
                    className={
                      version.state === 'active' ? 'lq-ap__rowActive' : undefined
                    }
                  >
                    <td className="lq-mono lq-nowrap">
                      {/* A draft has no version number, and that is by design rather than missing data:
                        an unpublished draft is not something an activation can pin to, and a `0.0.0`
                        placeholder would be a version number that looks real. */}
                      {semverOf(version) === null ? (
                        <span className="lq-muted">{t('policy.unversioned')}</span>
                      ) : (
                        semverOf(version)
                      )}
                    </td>
                    <td>
                      <span
                        className={`lq-tag lq-tag--state-${version.state}`}
                        title={t(`policy.stateHint.${version.state}`)}
                      >
                        <span className="lq-tag__dot" aria-hidden="true" />
                        {t(`policy.state.${version.state}`)}
                      </span>
                    </td>
                    <td>
                      <span className="lq-ap__hash lq-mono" title={version.content_hash}>
                        {shortHash(version.content_hash)}
                      </span>
                      {/* The two sentences that make this column the point of the screen: whether these
                        exact bytes are executing, and — when they are not — that the gap is the lazy
                        registry rather than a fault. */}
                      {isRunning && (
                        <div className="lq-ap__isRunning">{t('policy.hash.running')}</div>
                      )}
                      {version.state === 'active' && !isRunning && (
                        <div
                          className="lq-ap__notLoaded"
                          title={t(notLoadedReasonKey(abacEnabled))}
                        >
                          {t('policy.hash.notLoaded')}
                        </div>
                      )}
                    </td>
                    <td className="lq-table__numeric lq-ap__bytes">
                      {version.byte_length.toLocaleString()}
                    </td>
                    <td className="lq-nowrap">
                      {new Date(version.created_at).toLocaleString()}
                      {/* `created_by` is a subject id, so it is truncated with the whole value on hover
                        rather than rendered as a bare 36-character UUID in a cell somebody scans. */}
                      <div className="lq-cell__secondary" title={version.created_by}>
                        {t('policy.createdBy', { who: shortHash(version.created_by) })}
                      </div>
                    </td>
                    <td className="lq-table__actions">
                      {/* `lq-rowactions` rather than the bare cell: that class marks the column
                          and carries no layout at all, so without this the controls and the
                          divider between them rendered as one run-together word —
                          "ReactivateView rules". It is what `RulesScreen` already uses. */}
                      <span className="lq-rowactions">
                        {action === 'publish' && (
                          /* **Publishing is also the activation**, which is why there is no separate
                         Activate control on a draft: `PolicyService.publish` mints the semver and
                         makes that version the active bundle in one act. The Reactivate below is
                         for going back to an *older* published version. */
                          <div className="lq-ap__publish">
                            <label className="lq-ap__bumpField">
                              <span className="lq-ap__bumpLabel">
                                {t('policy.bump.label')}
                              </span>
                              <select
                                className="lq-ap__bump"
                                value={bump ?? ''}
                                disabled={busy}
                                onChange={(event) => {
                                  const chosen = event.target.value;
                                  setBump(chosen === '' ? null : (chosen as BumpKind));
                                }}
                              >
                                <option value="">{t('policy.bump.choose')}</option>
                                <option value="major">{t('policy.bump.major')}</option>
                                <option value="minor">{t('policy.bump.minor')}</option>
                                <option value="patch">{t('policy.bump.patch')}</option>
                              </select>
                            </label>
                            <button
                              type="button"
                              className="lq-rowaction"
                              disabled={busy || bump === null}
                              title={
                                bump === null
                                  ? t('policy.bump.required')
                                  : t('policy.publish')
                              }
                              onClick={() => {
                                if (bump === null) return;
                                publish.mutate(
                                  { bundleId: bundle.id, bump },
                                  {
                                    onSuccess: () => {
                                      onActed(
                                        'policy.acted.published',
                                        'policy.acted.publishedBody',
                                        {
                                          bundle: bundle.name,
                                        },
                                      );
                                    },
                                  },
                                );
                              }}
                            >
                              {t('policy.publish')}
                            </button>
                          </div>
                        )}
                        {action === 'reactivate' && semver !== null && (
                          <button
                            type="button"
                            className="lq-rowaction"
                            disabled={busy}
                            onClick={() => {
                              activate.mutate(
                                { bundleId: bundle.id, semver },
                                {
                                  onSuccess: () => {
                                    onActed(
                                      'policy.acted.reactivated',
                                      'policy.acted.reactivatedBody',
                                      { bundle: bundle.name, semver },
                                    );
                                  },
                                },
                              );
                            }}
                          >
                            {t('policy.reactivate')}
                          </button>
                        )}
                        {action === 'deprecate' && semver !== null && (
                          /* **Destructive, and the hint says how.** Withdrawing the only active
                         version leaves the tenant failing closed — every request a role would
                         permit is then refused. A control that consequential must not be
                         discoverable only by pressing it. */
                          <button
                            type="button"
                            className="lq-rowaction"
                            disabled={busy}
                            title={t('policy.deprecate.hint')}
                            onClick={() => {
                              deprecate.mutate(
                                { bundleId: bundle.id, semver },
                                {
                                  onSuccess: () => {
                                    onActed(
                                      'policy.acted.deprecated',
                                      'policy.acted.deprecatedBody',
                                      { bundle: bundle.name, semver },
                                    );
                                  },
                                },
                              );
                            }}
                          >
                            {t('policy.deprecate')}
                          </button>
                        )}
                        {action === 'none' && (
                          /* Said rather than left blank, and the two reasons are different: "already the
                         activated version" is the healthy case, "nothing to pin to" is a dead end. */
                          <span className="lq-muted" title={t(noActionKey(version))}>
                            {t(
                              version.state === 'active'
                                ? 'policy.noAction.activeLabel'
                                : 'policy.noAction.unpinnableLabel',
                            )}
                          </span>
                        )}
                        {/* **On every row, including a draft and a deprecated one.** The point of the
                        viewer is the versions a reader is *not* running: what a deprecated version
                        used to decide is the question an incident asks, and it was unanswerable.

                        Offered only where there is a semver to name, because the route addresses a
                        version by it — a draft has none, by design, so there is nothing to fetch.
                        Said rather than silently omitted. */}
                        {semver !== null ? (
                          <>
                            <span className="lq-rowactions__rule" aria-hidden="true" />
                            <button
                              type="button"
                              className="lq-rowaction"
                              aria-expanded={rulesOpen === semver}
                              onClick={() => {
                                setRulesOpen((open) => (open === semver ? null : semver));
                              }}
                            >
                              {t(
                                rulesOpen === semver
                                  ? 'policy.rules.close'
                                  : 'policy.rules.view',
                              )}
                            </button>
                          </>
                        ) : (
                          /* **Said in words, not as a tooltip on a space.** This branch was a
                           `title` on a `<span>` containing one space — unhoverable, unfocusable,
                           and announced as nothing, so the reason a draft has no rules view was
                           written down and unreachable. `lq-rowblocked` is the same treatment the
                           delete control uses for the same situation: a control that is absent for
                           a reason, with the reason on the page. */
                          <>
                            <span className="lq-rowactions__rule" aria-hidden="true" />
                            <span
                              className="lq-rowblocked"
                              title={t('policy.rules.draftWhy')}
                            >
                              {t('policy.rules.draftLabel')}
                            </span>
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                  {rulesOpen === semver && semver !== null && (
                    /* A full-width row beneath the version it belongs to, rather than inside the
                     actions cell. In a cell it would set that column's width and wrap to nothing;
                     as a row it gets the whole table and keeps the version above it as context,
                     which a dialog would cover. */
                    <tr>
                      <td colSpan={6} className="lq-ap__drawer">
                        <VersionRules bundleId={bundle.id} semver={semver} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="lq-note">{t('policy.versions.foot')}</p>
    </>
  );
}

/** What one version decides, read-only. **The surface this redesign exists for.**
 *
 * `GET .../versions/{semver}/policy` has served `{semver, authored_here, rules, rego}` for every
 * version since the module was built, and nothing rendered the rules: the only reachable view was
 * the *active* version's compiled Rego, buried in the builder. So "what does 2.2.0 decide" had no
 * answer for any version a reader was not currently running.
 *
 * **Four states, and three of them are not errors.** A failed read is about the request; a version
 * with no rules held here was uploaded pre-compiled and never had any; a version with an *empty*
 * rule list permits nothing at all, which is a fact about the policy and the sharpest thing this
 * viewer can tell somebody. Only the first is a fault.
 *
 * **Mounted on demand**, so a bundle with a long history costs one request when a reader asks
 * rather than one per row on render.
 */
function VersionRules({
  bundleId,
  semver,
}: {
  readonly bundleId: string;
  readonly semver: string;
}): ReactElement {
  const { t } = useTranslation();
  const policy = useVersionPolicy(bundleId, semver);

  return (
    <div className="lq-ap__rules">
      <div className="lq-ap__rulesHead">
        {/* **The heading labels the table rather than a `<caption>` repeating it.** A
            visually-hidden caption carrying the same sentence is read twice by a screen reader and
            counted twice by anything looking for the title — `aria-labelledby` gives the table the
            heading it already has. */}
        <span className="lq-ap__rulesTitle" id={`rules-${semver}`}>
          {t('policy.rules.title', { semver })}
        </span>
      </div>

      {policy.isPending && <SkeletonRows rows={2} columns={4} />}

      {/* A failed read is a failure of the request, never a statement about what the version
          decides. Reporting "no rules" from a 500 would be the defect this codebase records at
          four screens. */}
      {policy.isError && (
        <Alert tone="error" title={t('policy.rules.unread')}>
          {policy.error.message}
        </Alert>
      )}

      {policy.isSuccess && <RuleTable policy={policy.data} />}
    </div>
  );
}

/** The rules themselves, once read. Split out so the four states above stay legible. */
function RuleTable({ policy }: { readonly policy: VersionPolicy }): ReactElement {
  const { t } = useTranslation();
  const [regoOpen, setRegoOpen] = useState(false);

  /* **`authored_here: false` with both nulls is ordinary, not an error.** That version arrived as
     a pre-compiled artifact and this platform never held its rules, so there is nothing to lay
     out — and saying so is the difference between "we cannot show you" and "there is nothing". */
  if (policy.rules === null) {
    return <p className="lq-ap__rulesNote">{t('policy.rules.unavailable')}</p>;
  }

  /* **An empty rule list is not the same absence**, and it is the sharpest thing this viewer can
     say: the generated module permits only what a rule allows, so a version carrying no rules
     refuses everything. Rendering an empty table would leave a reader to infer that. */
  if (policy.rules.length === 0) {
    return <p className="lq-ap__rulesNote">{t('policy.rules.empty')}</p>;
  }

  return (
    <>
      <div className="lq-table-scroll">
        <table
          className="lq-table lq-table--ledger"
          aria-labelledby={`rules-${policy.semver}`}
        >
          <thead>
            <tr>
              <th scope="col">{t('policy.rules.role')}</th>
              <th scope="col">{t('policy.rules.action')}</th>
              <th scope="col">{t('policy.rules.resource')}</th>
              <th scope="col">{t('policy.rules.effect')}</th>
            </tr>
          </thead>
          <tbody>
            {policy.rules.map((rule, index) => (
              <tr
                /* The index is part of the key deliberately: two rules can legitimately differ
                   only in `resource_id`, and a key built from the other four would collide. */
                key={`${rule.role}:${rule.action}:${rule.resource_type}:${rule.resource_id ?? ''}:${String(index)}`}
              >
                <td className="lq-mono">{rule.role}</td>
                <td className="lq-mono">{rule.action}</td>
                <td className="lq-mono">
                  {rule.resource_type}
                  {/* **A resource-scoped rule is narrower than a type-wide one, and says so where
                      the scope is** rather than in a legend. This is the whole capability ABAC
                      adds over RBAC — one dataset rather than a type — so it must not read as a
                      footnote. */}
                  <span className="lq-ap__scoped">
                    {rule.resource_id === null
                      ? t('policy.rules.wholeType')
                      : t('policy.rules.scoped')}
                  </span>
                </td>
                <td>
                  {/* `deny` is the exception and reads like one; weight carries it as well as
                      colour, so the distinction survives being printed. */}
                  <span
                    className={
                      rule.effect === 'deny'
                        ? 'lq-ap__effect--deny'
                        : 'lq-ap__effect--allow'
                    }
                  >
                    {rule.effect}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Secondary and clearly labelled read-only. It is **not** regenerated from the rules above:
          the current generator might render them differently from whatever produced the module
          this version activates, so it is the bytes as compiled rather than a view of the table. */}
      {policy.rego !== null && (
        <p className="lq-note">
          <button
            type="button"
            className="lq-rowaction"
            aria-expanded={regoOpen}
            onClick={() => {
              setRegoOpen((open) => !open);
            }}
          >
            {t(regoOpen ? 'policy.rules.hideRego' : 'policy.rules.showRego')}
          </button>
        </p>
      )}
      {regoOpen && policy.rego !== null && (
        <>
          <p className="lq-note">{t('policy.rules.regoNote')}</p>
          <pre className="lq-ap__rego">{policy.rego}</pre>
        </>
      )}
    </>
  );
}

export function PolicyBundlesScreen(): ReactElement {
  const { t } = useTranslation();
  const bundles = usePolicyBundles();
  const active = useActiveBundles();
  const [selected, setSelected] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  /* **One confirmation open at a time, keyed on the bundle id.** Two open confirmations on a
     destructive action is how somebody presses the wrong one; a single key means opening the next
     closes the last. Lifted here rather than held in the control because the confirmation renders
     as a sibling `<tr>`, which only the table body can place. */
  const [deleting, setDeleting] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    readonly titleKey: string;
    readonly bodyKey: string;
    readonly values: Record<string, string>;
  } | null>(null);

  const rows = bundles.isSuccess ? bundles.data : [];
  const looked = rows.slice(0, MAX_VERSION_LOOKUPS);

  /* One read per bundle for the table's own two columns, bounded by `MAX_VERSION_LOOKUPS`. `useQueries`
   * rather than a loop of hooks, because the number of bundles is data and a hook count cannot be. */
  const versionReads = useQueries({
    queries: looked.map((bundle) => versionsQuery(bundle.id)),
  });
  const versionsById = new Map<string, readonly BundleVersion[]>();
  looked.forEach((bundle, index) => {
    const read = versionReads[index];
    if (read?.isSuccess === true) versionsById.set(bundle.id, read.data);
  });

  const runningHashes = runningHashesOf(active.isSuccess ? active.data.items : []);
  const governingName = active.isSuccess ? active.data.authzBundleName : null;

  /** The bundle whose panels are shown. **Defaults to one rather than to nothing.**
   *
   * It used to default to `null`, which meant the rule builder — the primary thing this screen is
   * for — rendered nothing at all until somebody clicked a row action labelled "Show versions".
   * A person landing here saw a table and two banners and reasonably concluded there was no way to
   * make a policy. That is a discoverability failure rather than a missing feature, and it is the
   * worse of the two because the feature looked absent.
   *
   * The **governing** bundle is preferred over the first, because it is the only one whose rules
   * decide anything: `authz` is the one name authorization consults, and opening the builder on a
   * bundle that governs nothing would invite somebody to build a whole policy in the wrong place.
   * Falling back to the first when no bundle carries that name, so the builder is still reachable
   * on a tenant that has not created it yet — with the row marker below saying what that costs.
   */
  const preferred =
    rows.find((bundle) => governingName !== null && bundle.name === governingName) ??
    rows[0];
  const selectedBundle =
    rows.find((bundle) => bundle.id === selected) ?? preferred ?? null;

  const header = (
    <PageHeader
      title={t('policy.title')}
      lede={t('policy.intro')}
      meta={
        <>
          {/* The counts only. **The runtime line that used to live here was the defect**: it
              read `runtime_available` and announced "Policy runtime available", which says the
              wheel is installed and nothing about whether policy decides anything. The banner
              below answers that question properly, and two places answering it differently is
              worse than one answering it wrongly. */}
          {bundles.isSuccess && (
            <div>
              {t('policy.meta.counts', {
                stored: rows.length,
                instantiated: active.isSuccess ? active.data.items.length : 0,
              })}
            </div>
          )}
        </>
      }
    />
  );

  return (
    <div className="lq-ap lq-refscreen">
      {header}
      <div className="lq-pagerule" />

      {/* **The first thing on the page, because it is what every other reading depends on.** A
          version marked active means nothing if the evaluator is unwired, and this is the only
          element that can say so. */}
      <EnforcementBanner />

      {/* **Directly under the banner, because the banner states the situation and this changes
          it.** The specification's requirement is that a reader never has to infer the tenant's
          posture from an error: the banner explains what is happening, this says what mode the
          tenant is in and lets an administrator change it, and the three operations are named
          together so nobody reaches for deprecation as an off switch. */}
      <EnforcementModePanel />

      {/* Always. The absence of an upload control is a decision, and a decision nobody is told about
          reads as an unfinished screen. */}
      <div className="lq-band lq-band--info lq-band--closing">
        <div className="lq-band__title">{t('policy.noUpload.title')}</div>
        <div className="lq-band__body">{t('policy.noUpload.body')}</div>
      </div>

      <SectionHead
        title={t('policy.section.stored')}
        action={
          /* Ghost rather than accent, as the reference has it, and the distinction is honest: this
             opens a form that registers a name. The accent-filled control on this platform is for the
             act that changes what happens, and creating an identity changes nothing. */
          <button
            type="button"
            className="lq-sect__ghost"
            onClick={() => {
              setFormOpen((open) => !open);
              setMessage(null);
            }}
          >
            {t(formOpen ? 'policy.form.close' : 'policy.form.open')}
          </button>
        }
      />

      {message !== null && (
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">
            {t(message.titleKey, message.values)}
          </strong>
          <span className="lq-flash__body">{t(message.bodyKey, message.values)}</span>
        </div>
      )}

      {formOpen && (
        <CreateBundleForm
          governingName={governingName}
          onClose={() => {
            setFormOpen(false);
          }}
          onCreated={(name) => {
            setFormOpen(false);
            setMessage({
              titleKey: 'policy.acted.created',
              bodyKey: 'policy.acted.createdBody',
              values: { bundle: name },
            });
          }}
        />
      )}

      {bundles.isPending && <SkeletonRows rows={3} columns={5} />}

      {bundles.isError && (
        <Alert tone="error" title={t('policy.error.title')}>
          {bundles.error.message}
        </Alert>
      )}

      {bundles.isSuccess &&
        (rows.length === 0 ? (
          <EmptyState
            title={t('policy.empty.title')}
            guidance={t('policy.empty.guidance')}
          />
        ) : (
          <div className="lq-table-scroll lq-table-scroll--framed">
            <table className="lq-table lq-table--ledger">
              <caption className="lq-visually-hidden">{t('policy.caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('policy.column.name')}</th>
                  <th scope="col">{t('policy.column.description')}</th>
                  <th scope="col">{t('policy.column.versions')}</th>
                  <th scope="col">{t('policy.column.running')}</th>
                  <th scope="col" className="lq-table__actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((bundle) => {
                  const versions = versionsById.get(bundle.id);
                  /* Read from the bundle whose panels are actually shown, not from the raw
                     selection state. Those differ before anybody clicks: the screen opens on a
                     bundle by default, so keying the highlight on `selected` left the builder
                     open for a bundle no row was marked as — and the row's control still offered
                     to "Open" the one already open. One source of truth for both. */
                  const chosen = bundle.id === selectedBundle?.id;

                  return (
                    /* The whole row selects, as the reference has it. The keyboard route is the
                       action button in the last cell rather than anything on the row: a `tr` with an
                       `onClick` and a `tabIndex` announces itself as a control and is not one, and a
                       button inside a clickable row fires the handler twice. */
                    <Fragment key={bundle.id}>
                      <tr
                        className={
                          chosen ? 'lq-ap__row lq-ap__row--chosen' : 'lq-ap__row'
                        }
                        onClick={() => {
                          setSelected(bundle.id);
                          setMessage(null);
                        }}
                      >
                        <td
                          className={
                            chosen ? 'lq-ap__name lq-ap__name--chosen' : 'lq-ap__name'
                          }
                        >
                          {bundle.name}
                          {governingName !== null && bundle.name !== governingName && (
                            /* **The fact a reader most needs on this table and could not get.**
                             Authorization consults exactly one bundle name, so rules built in any
                             other bundle are stored, versioned, published, activated — and
                             consulted by nothing. The enforcement banner says this, and only when
                             attribute scoping is switched on; somebody building a policy needs it
                             beforehand, which is when the name can still be changed cheaply. */
                            <span
                              className="lq-ap__governs"
                              title={t('policy.governsNothing.why', {
                                name: governingName,
                              })}
                            >
                              {t('policy.governsNothing')}
                            </span>
                          )}
                        </td>
                        <td className="lq-cell--quiet">
                          {bundle.description === '' ? (
                            <span className="lq-muted">{t('policy.noDescription')}</span>
                          ) : (
                            bundle.description
                          )}
                        </td>
                        <td className="lq-cell--quiet lq-nowrap">
                          {versions === undefined ? (
                            <span className="lq-muted">{t('policy.run.unread')}</span>
                          ) : (
                            t('policy.versionCount', { count: versions.length })
                          )}
                        </td>
                        <td className="lq-nowrap">
                          <RunChip versions={versions} runningHashes={runningHashes} />
                        </td>
                        <td className="lq-table__actions">
                          {/* Same reason as the versions table: without a flex parent "Selected"
                              and "Delete" render as "SelectedDelete". */}
                          <span className="lq-rowactions">
                            <button
                              type="button"
                              className="lq-rowaction"
                              disabled={chosen}
                              onClick={(event) => {
                                /* The row handler would run too, which is harmless here — both select the
                               same bundle — and would not be if a future action did anything else. */
                                event.stopPropagation();
                                setSelected(bundle.id);
                                setMessage(null);
                              }}
                            >
                              {t(chosen ? 'policy.selected' : 'policy.openBundle')}
                            </button>
                            <span className="lq-rowactions__rule" aria-hidden="true" />
                            {/* **Every bundle, including the governing one.** The alternative is a
                            screen on which an identity created by mistake can never be removed —
                            and for `authz` that made adopting attribute policy a one-way door. The
                            control refuses itself while a version is active, with the reason on it
                            rather than behind a press. */}
                            <DeleteBundleControl
                              versions={versions}
                              open={deleting === bundle.id}
                              onOpen={() => {
                                setDeleting((open) =>
                                  open === bundle.id ? null : bundle.id,
                                );
                              }}
                            />
                          </span>
                        </td>
                      </tr>
                      {deleting === bundle.id && versions !== undefined && (
                        <tr>
                          <DeleteConfirmRow
                            bundle={bundle}
                            versions={versions}
                            onCancel={() => {
                              setDeleting(null);
                            }}
                            onDeleted={(name, note) => {
                              setDeleting(null);
                              if (chosen) setSelected(null);
                              setMessage({
                                titleKey: 'policy.acted.deleted',
                                bodyKey: 'policy.acted.deletedBody',
                                values: { bundle: name, note },
                              });
                            }}
                          />
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}

      <p className="lq-note">{t('policy.stored.foot')}</p>
      {rows.length > MAX_VERSION_LOOKUPS && (
        /* Stated rather than silent. Past the cap two columns read "not read", and a reader who was
           not told would take that for a fact about the bundle. */
        <p className="lq-note">
          {t('policy.stored.capped', { cap: MAX_VERSION_LOOKUPS, total: rows.length })}
        </p>
      )}

      {selectedBundle !== null && (
        <>
          <SectionHead
            title={t('policy.section.versions', { bundle: selectedBundle.name })}
          />
          <VersionsTable
            bundle={selectedBundle}
            runningHashes={runningHashes}
            onActed={(titleKey, bodyKey, values) => {
              setMessage({ titleKey, bodyKey, values });
            }}
          />

          {/* **Above the activation history and below the versions, which is the order of the
              work.** Somebody writes a policy, sees it appear as a draft in the table above,
              publishes it, and then reads the history to confirm what it replaced. Putting the
              editor last would put the first step at the bottom of the page. */}
          <SectionHead title={t('policy.build.title')} />
          <PolicyBuilder
            bundle={selectedBundle}
            /* The active version, so the editor opens what is *running* rather than the starter —
               and `null` when nothing is active, which is the ordinary state of a bundle nobody
               has published. Read from the versions the table already loaded rather than from
               `/active`: that route reports what this *process* instantiated, which is empty until
               something evaluates, so it would send an author back to the starter template beside
               a version they had just activated. */
            activeSemver={activeSemverOf(selectedBundle.id, versionsById)}
          />

          {/* Under the versions rather than beside them: the versions are what a reader acts on
              and this is what they check afterwards — "who made this active, and what did it
              replace". Scoped to the selected bundle, because an activation is about one bundle. */}
          <SectionHead title={t('policy.activations.title')} />
          <ActivationsPanel bundle={selectedBundle} />
        </>
      )}

      <SectionHead title={t('policy.section.running')} />
      <div className="lq-ap__panels">
        <section className="lq-ap__panel">
          <h3 className="lq-ap__panelHead">{t('policy.running.title')}</h3>
          {active.isPending && <SkeletonRows rows={2} columns={2} />}
          {active.isError && <Alert tone="error">{t('policy.running.failed')}</Alert>}
          {active.isSuccess &&
            (active.data.items.length === 0 ? (
              /* The normal state on a quiet process, and the copy says so: the registry is a lazy
                 cache, so nothing instantiated is not nothing configured. */
              <p className="lq-ap__panelBody">{t('policy.running.empty')}</p>
            ) : (
              active.data.items.map((loaded) => (
                <div key={loaded.content_hash} className="lq-ap__loaded">
                  <div className="lq-ap__loadedName lq-mono">{loaded.bundle_name}</div>
                  {/* **Which version, and when it was last re-read.** Both were unanswerable from
                      this panel: a reader could see that something was running and not which
                      version, so "is the process on the version I activated" needed the stored
                      list and a hash comparison. With the staleness note below, `loaded` also
                      turns that window into a figure a reader can check. */}
                  <div className="lq-ap__loadedHash lq-mono" title={loaded.content_hash}>
                    {shortHash(loaded.content_hash)}
                  </div>
                  <div className="lq-cell__secondary">
                    {t('policy.loaded.version', {
                      semver: loaded.semver,
                      when: new Date(loaded.loaded_at).toLocaleString(),
                    })}
                  </div>
                </div>
              ))
            ))}
          {active.isSuccess && (
            <p className="lq-ap__panelNote">
              {t('policy.running.staleness', {
                seconds: active.data.maxStalenessSeconds,
              })}
            </p>
          )}
        </section>

        <section className="lq-ap__panel">
          <h3 className="lq-ap__panelHead">{t('policy.agree.title')}</h3>
          {rows.length === 0 ? (
            <p className="lq-ap__panelBody">{t('policy.agree.nothing')}</p>
          ) : (
            looked.map((bundle) => {
              const row = agreementFor(
                bundle,
                versionsById.get(bundle.id),
                runningHashes,
              );
              return (
                <div key={row.bundleId} className="lq-ap__agree">
                  <span
                    className={`lq-ap__mark lq-ap__mark--${row.agreement}`}
                    aria-hidden="true"
                  >
                    {AGREEMENT_MARK[row.agreement]}
                  </span>
                  <div className="lq-ap__agreeBody">
                    <strong className="lq-ap__agreeName">{row.bundleName}</strong>
                    <div className="lq-ap__agreeNote">
                      {row.hash === null
                        ? t(`policy.agree.${row.agreement}`)
                        : t(`policy.agree.${row.agreement}`, { hash: row.hash })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </section>
      </div>
    </div>
  );
}
